import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Package, AlertTriangle, Search, Filter, ChevronLeft, ChevronRight,
  RefreshCw, Download, ArrowUpDown, Box, TrendingDown, CheckCircle2
} from 'lucide-react';
import * as XLSX from 'xlsx';

const WMSStockPage = () => {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (stockFilter !== 'all') params.stock_filter = stockFilter;
      if (category) params.category = category;
      const res = await api.get('/stock/status', { params });
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [page, search, stockFilter, category]);

  const fetchDataQuiet = useCallback(async () => {
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (stockFilter !== 'all') params.stock_filter = stockFilter;
      if (category) params.category = category;
      const res = await api.get('/stock/status', { params });
      setData(res.data);
    } catch {}
  }, [page, search, stockFilter, category]);

  // Real-time: refresh stock data on stock changes
  useRealtimeEvent('stock_change', fetchDataQuiet);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/consumable-categories');
        setCategories(res.data);
      } catch {}
    };
    fetchCategories();
  }, []);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const sortedItems = [...(data.items || [])].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleExport = () => {
    try {
      const exportData = sortedItems.map(item => ({
        'Cod': item.code,
        'Denumire': item.name,
        'Categorie': item.category_name || '-',
        'UM': item.unit,
        'Stoc': item.total_stock,
        'Stoc Minim': item.min_stock,
        'Locatii BIN': item.bin_count,
        'Status': item.is_zero ? 'FARA STOC' : item.is_low ? 'STOC MINIM' : 'OK'
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Situatie Stoc');
      XLSX.writeFile(wb, `situatie_stoc_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export generat!');
    } catch {
      toast.error('Eroare la export');
    }
  };

  const totalItems = data.total || 0;
  const lowCount = sortedItems.filter(i => i.is_low && !i.is_zero).length;
  const zeroCount = sortedItems.filter(i => i.is_zero).length;
  const okCount = sortedItems.filter(i => !i.is_low).length;

  return (
    <div className="space-y-4" data-testid="wms-stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Situatie Stoc</h1>
          <p className="text-slate-500 text-sm mt-1">{totalItems} articole in nomenclator</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Reincarca
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button onClick={() => { setStockFilter('all'); setPage(1); }} className={`p-3 rounded-lg border text-left transition-all ${stockFilter === 'all' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-blue-300'}`}>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-slate-500">Total</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{totalItems}</p>
        </button>
        <button onClick={() => { setStockFilter('ok'); setPage(1); }} className={`p-3 rounded-lg border text-left transition-all ${stockFilter === 'ok' ? 'border-green-500 bg-green-50 ring-1 ring-green-500' : 'border-slate-200 hover:border-green-300'}`}>
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-slate-500">In stoc</span>
          </div>
          <p className="text-xl font-bold text-green-600">{okCount}</p>
        </button>
        <button onClick={() => { setStockFilter('low'); setPage(1); }} className={`p-3 rounded-lg border text-left transition-all ${stockFilter === 'low' ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' : 'border-slate-200 hover:border-orange-300'}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-medium text-slate-500">Stoc minim</span>
          </div>
          <p className="text-xl font-bold text-orange-600">{lowCount}</p>
        </button>
        <button onClick={() => { setStockFilter('zero'); setPage(1); }} className={`p-3 rounded-lg border text-left transition-all ${stockFilter === 'zero' ? 'border-red-500 bg-red-50 ring-1 ring-red-500' : 'border-slate-200 hover:border-red-300'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-xs font-medium text-slate-500">Fara stoc</span>
          </div>
          <p className="text-xl font-bold text-red-600">{zeroCount}</p>
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cauta dupa denumire sau cod articol..."
                className="pl-10"
                data-testid="stock-search-input"
              />
            </div>
            <Select value={category || 'all'} onValueChange={v => { setCategory(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid="stock-category-filter">
                <SelectValue placeholder="Toate categoriile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate categoriile</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} size="sm">
              <Filter className="w-4 h-4 mr-1" /> Filtreaza
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">Niciun articol gasit</p>
              <p className="text-sm mt-1">Incercati sa modificati filtrele</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stock-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('code')}>
                      <div className="flex items-center gap-1">Cod <ArrowUpDown className="w-3 h-3" /></div>
                    </th>
                    <th className="text-left p-3 font-semibold text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                      <div className="flex items-center gap-1">Denumire <ArrowUpDown className="w-3 h-3" /></div>
                    </th>
                    <th className="text-left p-3 font-semibold text-slate-600">Categorie</th>
                    <th className="text-center p-3 font-semibold text-slate-600">UM</th>
                    <th className="text-right p-3 font-semibold text-slate-600 cursor-pointer select-none" onClick={() => toggleSort('total_stock')}>
                      <div className="flex items-center justify-end gap-1">Stoc <ArrowUpDown className="w-3 h-3" /></div>
                    </th>
                    <th className="text-right p-3 font-semibold text-slate-600">Min</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Locatii</th>
                    <th className="text-center p-3 font-semibold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.id} className={`border-b hover:bg-slate-50/50 transition-colors ${item.is_zero ? 'bg-red-50/30' : item.is_low ? 'bg-orange-50/30' : ''}`}>
                      <td className="p-3 font-mono text-xs text-slate-600">{item.code}</td>
                      <td className="p-3">
                        <p className="font-medium text-slate-900 truncate max-w-xs">{item.name}</p>
                      </td>
                      <td className="p-3 text-slate-500 text-xs">{item.category_name || '-'}</td>
                      <td className="p-3 text-center text-slate-500">{item.unit}</td>
                      <td className="p-3 text-right">
                        <span className={`text-lg font-bold ${item.is_zero ? 'text-red-600' : item.is_low ? 'text-orange-600' : 'text-slate-900'}`}>
                          {item.total_stock}
                        </span>
                      </td>
                      <td className="p-3 text-right text-slate-500">{item.min_stock}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Box className="w-3 h-3 text-slate-400" />
                          <span className="text-xs">{item.bin_count}</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {item.is_zero ? (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">FARA STOC</Badge>
                        ) : item.is_low ? (
                          <Badge className="bg-orange-100 text-orange-700 text-[10px]">STOC MINIM</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t bg-slate-50">
              <p className="text-xs text-slate-500">
                Pagina {data.page} din {data.total_pages} ({data.total} articole)
              </p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.min(5, data.total_pages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, data.total_pages - 4));
                  const p = start + i;
                  if (p > data.total_pages) return null;
                  return (
                    <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(p)} className="w-8 h-8 p-0">
                      {p}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WMSStockPage;
